/**
 * 報價單 PDF 匯出 — 依照 a2-報價單實際畫面-示意圖.html
 * 需求 R3：以另開分頁方式呼叫瀏覽器列印，不再使用同頁隱藏 iframe
 */

// 需求 R96：備註欄（葷素備註在上、業務備註在下）的組行規則與 Excel 匯出共用同一份實作，
// 避免兩條獨立路徑各寫一次而漂移成「一邊兩行、一邊一行」。
import { buildRemarkLines } from './quoteRemark';
// 需求 R111：金額欄（單價／箱價）的幣別位數與無條件進位規則同樣與 Excel 匯出共用一份，
// 理由同上——PDF 與 Excel 是兩條獨立路徑，只改一邊就會漂移成「PDF 對、Excel 錯」。
import { fmtMoney, calcBoxPrice } from './quoteMoney';
// 需求 R165 Q1=A（PM 2026-09-07 改判）：箱價回到「印出來的單價 × 箱入數」，不再需要
// quoteUnitPrice 的未進位基準（resolveBoxUnitPriceBase），故本檔已移除該 import。
// 需求 R121 R2：保存期限的取值（快照 → populate 產品 → 實時產品）三層 fallback 與 Excel 共用，
// 理由同上——編輯頁匯出重組的 quote 物件沒有 populate，只讀 item.product.shelfLife 會整欄空白。
import { getItemShelfLife } from './quoteShelfLife';

// 非金額數字（材積 cuft 等）用的通用格式化，預設一位小數。
// 需求 R111：金額欄已改用 fmtMoney(value, currency)，不再走這支——這裡的 d = 1 預設值
// 正是 PM 回報「美金匯出只顯示到小數第 1 位」的根因（原本單價／箱價都沒帶第二參數）。
// 需求 R139（PM 2026-08-25 拍板 Q1=A）：材積 cuft 改在呼叫點帶 fmt(cuft, 2)，
// 位數不足補零（1.2 → 1.20），與畫面 parseFloat(...).toFixed(2) 一致。預設值刻意維持 1，
// 只改那一個呼叫點，避免誤傷其它沿用預設的呼叫者。
function fmt(v, d = 1) {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function esc(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export async function exportQuotePdf({ quote }) {
  const co = await fetchCompanyInfo(quote.companyHeader);
  const html = buildQuoteHtml(quote, co, { autoPrint: true });

  // 需求 R3：以 _blank 另開分頁載入報價單 HTML，並在新分頁內呼叫 window.print()
  // 這樣瀏覽器的列印預覽是疊在「新分頁」上，而不是壓在原本的報價單詳細頁
  const win = window.open('', '_blank');
  if (!win) {
    // 瀏覽器封鎖彈出視窗時的回退提示
    throw new Error('瀏覽器封鎖了新分頁，請允許此網站開啟彈出視窗後再試一次');
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

export async function previewQuote({ quote }) {
  const co = await fetchCompanyInfo(quote.companyHeader);
  const html = buildQuoteHtml(quote, co);
  const win = window.open('', '_blank');
  win.document.open();
  win.document.write(html);
  win.document.close();
}

async function fetchCompanyInfo(companyLabel) {
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch('/api/companies', { headers });
    if (!res.ok) throw new Error('Failed to fetch companies');
    const companies = await res.json();
    const match = companies.find(c => c.label === companyLabel);
    if (match) {
      return {
        name: match.name || match.label,
        nameEn: match.nameEn || '',
        logo: match.label,
        logoUrl: match.logoUrl || '',
        address: match.address || '',
        addressEn: match.addressEn || '',
        tel: [match.tel ? `Tel: ${match.tel}` : '', match.fax ? `Fax: ${match.fax}` : ''].filter(Boolean).join(' | '),
      };
    }
    // 找不到對應公司時用第一筆作為 fallback
    const fallback = companies[0];
    if (fallback) {
      return {
        name: fallback.name || fallback.label,
        nameEn: fallback.nameEn || '',
        logo: fallback.label,
        logoUrl: fallback.logoUrl || '',
        address: fallback.address || '',
        addressEn: fallback.addressEn || '',
        tel: [fallback.tel ? `Tel: ${fallback.tel}` : '', fallback.fax ? `Fax: ${fallback.fax}` : ''].filter(Boolean).join(' | '),
      };
    }
  } catch (err) {
    console.error('[quotePdfExport] fetchCompanyInfo failed:', err);
  }
  return { name: '', nameEn: '', logo: '', logoUrl: '', address: '', addressEn: '', tel: '' };
}

function buildQuoteHtml(quote, co, opts = {}) {
  const { autoPrint = false } = opts;
  const items = quote.items || [];
  const contact = quote.contactPerson || {};
  const currency = quote.currency || 'TWD';
  const currLabel = { TWD: 'NT$', HKD: 'HK$', USD: 'US$', EUR: '€' }[currency] || currency;
  const custContact = (quote.customer?.contacts || [])[0] || {};
  const custPhone = custContact.phone || '';
  const custEmail = custContact.email || '';

  const rows = items.map(item => {
    // 需求 R140 Q2=A（PM 2026-08-28）：匯出**讀 DB 存值、不重算**。需求 R132（b981707）之前
    // 存檔的舊單，其 unitPrice 由舊公式（成本被進位兩次）算出，四種幣別都可能高一個進位單位
    // （TWD/HKD 0.1、USD/EUR 0.01）；PM 拍板 A＝不主動回寫，要等使用者開編輯頁重存才更新。
    // 詳情頁 QuoteDetailPage 與 Excel 匯出同此規則，三處刻意一致，勿只改一處。
    const unitPrice = item.unitPrice;
    // 需求 R165 Q1=A（PM 2026-09-07 拍板，改判 需求 R140 Q1=B、回到 需求 R111 Q3=A 的方向）：
    // 箱價＝**上面那個印出來的單價** × 箱入數，使用者拿計算機一定驗算得出來
    // （PM 逐字：0.72*12 = 8.64、0.82*12 = 9.84、0.49*24 = 11.76）。
    // 基準直接傳 item.unitPrice：calcBoxPrice 內部會先套與單價欄 fmtMoney 同一個 ceilMoney，
    // 兩者逐字同源，所以印出來的單價一個字都不會變（需求 R140 Q2=A 仍成立）。
    // 與 Excel 匯出刻意同一寫法，勿只改一處（需求 R76 的教訓）。
    const boxPrice = calcBoxPrice(item.unitPrice, item.pcsPerBox, currency);
    // 需求 R139（PM 2026-08-25）：cuft 顯示 2 位（下方 fmt(cuft, 2)）。
    // Q3 逐字「比照 需求 R70 價格規則與 cuft 無關，進位方式依照產品資料中，由外箱尺寸換算
    // 出來的結果來進位」→ 匯出端不再自行進位／取捨，直接顯示 item.volumeCuft 快照本身
    // （來源 server/utils/volume.js calcCuft 已四捨五入到 2 位），故金額欄的 ceilMoney 不套用在這裡。
    // Q4：舊報價單一律以既有快照值顯示，不重算材積。
    // Q2=A：CBM 維持 toFixed(3) 三位小數不動。
    const cuft = item.volumeCuft;
    const cbm = cuft != null ? (cuft * 0.0283168).toFixed(3) : '—';
    const volMethod = item.volumeMethod || item.product?.volumeMethod || 'auto';

    const imgUrl = item.product?.productImage
      ? (item.product.productImage.startsWith('http') ? item.product.productImage : `${window.location.origin}${item.product.productImage}`)
      : '';
    const thumbContent = imgUrl
      ? `<img src="${esc(imgUrl)}" style="width:100%;height:100%;object-fit:contain;border-radius:4px;" />`
      : '產品圖';

    // 需求 R37：PM 2026-05-25 確認「條碼僅為數字字串」。
    // 強制轉 String + trim，避免數字型 barcode 在 esc/拼接時意外丟值。
    const rawBarcode = item.barcode != null && item.barcode !== ''
      ? item.barcode
      : item.product?.barcode;
    const barcode = rawBarcode != null ? String(rawBarcode).trim() : '';
    // 需求 R121 R2：改走共用取值——原本只讀 item.product?.shelfLife，
    // 編輯頁匯出（buildExportQuoteObj）沒有 populate 就永遠取不到值。
    const shelfLife = getItemShelfLife(item);
    return `<tr>
      <td>
        <div class="product-cell">
          <div class="product-thumbnail">${thumbContent}</div>
          <div class="product-info">
            <div class="product-code">${esc(item.productCode || item.product?.productCode || '')}</div>
            <div class="product-name">${esc(item.productName)}</div>
            <div class="product-name-en">${esc(item.englishName)}</div>
          </div>
        </div>
      </td>
      <td class="text-center" style="font-size:12px;line-height:1.4;">
        ${item.spec && item.pcsPerBox ? esc(item.spec) + ' x ' + item.pcsPerBox + '入/箱' : esc(item.spec || '—')}
        ${item.moq ? '<br>MOQ: ' + esc(item.moq) : ''}
        ${shelfLife ? '<br>' + esc(shelfLife) : ''}
      </td>
      <td class="text-center" style="font-size:12px;line-height:1.4;">
        ${unitPrice != null ? currLabel + ' ' + fmtMoney(unitPrice, currency) : '—'}
        ${boxPrice != null ? '<br><span style="color:#8D6E63;">' + currLabel + ' ' + fmtMoney(boxPrice, currency) + '/箱</span>' : ''}
      </td>
      <td class="text-center">${cuft != null ? (volMethod === 'estimate' ? '<span style="color:#ef4444;">' + fmt(cuft, 2) + ' / ' + cbm + '</span>' : fmt(cuft, 2) + ' / ' + cbm) : '—'}${barcode ? '<br><span style="color:#8D6E63;font-size:12px;">' + esc(barcode) + '</span>' : ''}</td>
      <td class="text-center">${buildRemarkLines(item).map(esc).join('<br>')}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8">
<title>${esc(quote.quoteNumber || '報價單')}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;700&display=swap" rel="stylesheet">
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
/* 需求 R37：CDN 取不到 Noto Sans TC 時，依序回退到使用者作業系統內建 CJK 字型，
   避免列印時整段中文以系統預設拉丁字型回退而出現 □ 或亂碼。 */
body { font-family: 'Noto Sans TC', 'Microsoft JhengHei', 'PingFang TC', 'Heiti TC', 'Source Han Sans TC', 'Noto Sans CJK TC', -apple-system, BlinkMacSystemFont, sans-serif; color: #5D4037; background: #e0e0e0; }

.quotation-container { width: 210mm; margin: 0 auto; background: #fff; }

.quotation-header {
  padding: 24px;
  border-bottom: 2px solid #FFD54F;
  background: linear-gradient(135deg, #FFFBF0, #FFECB3);
}
.header-top { display: flex; justify-content: space-between; align-items: stretch; margin-bottom: 24px; }
.company-info { display: flex; align-items: center; gap: 16px; }
/* 需求 R135 Q3=放大 + Q5=PDF 同步：Logo 80px → 92px，與 Excel 的 LOGO_BOX_PX 同值
   （Excel 端 A 欄由 89px 加寬到 103px，Logo 依同比例 80→92）。兩邊都是 object-fit/fit-contain，
   非正方形 Logo 只會等比放大不變形。header-top 為 flex space-between，多出的 12px 由
   .company-details 這側吸收，不會擠壓右側標題色塊／聯絡人區。 */
.company-logo {
  width: 92px; height: 92px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 18px; color: #8D6E63; flex-shrink: 0;
  overflow: hidden;
}
.company-logo img {
  width: 100%; height: 100%; object-fit: contain;
}
.company-details h1 { font-size: 20px; color: #5D4037; margin-bottom: 4px; }
.company-details p { font-size: 12px; color: #8D6E63; margin: 0; }
.company-details p.company-name-en { font-size: 14px; }

.quotation-title { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; }
.quotation-title h2 {
  font-size: 14px; color: #5D4037; background: #FFD54F;
  padding: 4px 8px; border-radius: 4px; white-space: nowrap;
}
.contact-display { margin-top: 4px; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.contact-display .contact-label { font-size: 12px; color: #8D6E63; }
.contact-display .contact-name { font-size: 14px; font-weight: 500; color: #5D4037; }
.contact-display .contact-info { font-size: 12px; color: #8D6E63; }

.header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.info-section {
  background: rgba(255,255,255,0.7); padding: 16px; border-radius: 4px;
  border: 1px solid #e6c9a3;
}
.info-section h3 { font-size: 14px; color: #8D6E63; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
/* 需求 R134（PM 2026-08-21 Q1=B「Excel 與 PDF 一起改成值靠左」）：
   原本 justify-content:space-between 會把值推到最右邊，與 Excel 舊版的 horizontal:'right' 同病。
   改為標籤固定寬 90px（≈ Excel A/D 欄 12ch = 89px）、值緊接其後靠左，
   三列的值因此對齊成一直線，與 Excel 的 B:C／E:F 合併格左對齊呈現一致。
   Q2=A：值沿用與標籤相同的縮排基準，不額外貼齊區塊邊界。 */
.info-row { display: flex; justify-content: flex-start; padding: 4px 0; border-bottom: 1px dashed #e6c9a3; }
.info-row:last-child { border-bottom: none; }
.info-label { font-weight: 500; color: #8D6E63; font-size: 14px; flex: 0 0 90px; }
.info-value { font-weight: 500; color: #5D4037; font-size: 14px; flex: 1 1 auto; text-align: left; }

.quotation-body { padding: 8px 24px; }
.items-table { width: 100%; border-collapse: collapse; }
.items-table thead { background: #FFECB3; }
.items-table th {
  padding: 8px 6px; text-align: left; font-weight: 500; font-size: 13px;
  color: #5D4037; border-bottom: 2px solid #FFD54F; vertical-align: middle;
}
.items-table th .th-en { font-size: 10px; font-weight: 400; color: #8D6E63; }
.items-table th.text-center, .items-table td.text-center { text-align: center; }
.items-table tbody tr { border-bottom: 1px solid #e6c9a3; }
.items-table td { padding: 10px 8px; font-size: 13px; vertical-align: middle; line-height: 1.4; }
/* 需求 R135 Q5（PM 2026-08-24「請同步調整」）：Excel 品項列高 70pt → 72pt
   （ITEM_ROW_HEIGHT，≈ 96px @96DPI）。PDF 這邊以 td 的 height 對齊同一個視覺高度——
   在表格排版中 height 等同 min-height，內容超過時仍會自然撐高，不會裁切。
   實測（headless Chrome，210mm 頁寬）：改前列高 88.7px（由 55px 縮圖主導）→ 改後 96px。
   需求 R135 追加（PM 2026-08-27「決議調整高度由原本調整的 72 加高至 108」）：
   Excel ITEM_ROW_HEIGHT 72pt → 108pt，本處同步 96px → 144px（＝ 108 × 4/3 @96DPI）。 */
.items-table tbody td { height: 144px; }

/* 需求 R161（PM 2026-09-06 拍板 Q1=A「接受」、Q3=A「維持每頁重複」）：
   明細是一張連續的 <table>，而在本規則加入之前，全檔沒有任何 break-inside／page-break-inside，
   Chrome 列印時因此被允許在 <tr> 內部切頁 —— 附圖 page4→page5 就是同一筆 B182639 被撕成
   「上一頁只有貨編＋右側四欄、下一頁只有縮圖＋中英品名而右側四欄全空」的兩截殘列。
   需求 R135 把列高由 96px 提高到 144px 之後，落在頁尾剩餘空間不足的機率與撕裂幅度都變大，
   這也是為什麼問題現在才明顯浮現（根因仍是缺這條規則，不是列高本身）。
   兩個屬性都寫：break-inside 是現行標準、page-break-inside 是舊版 WebKit 相容寫法。
   tr 與 td 都要 —— 只鎖 tr 在部分 Chrome 版本仍會由儲存格內部的 block 盒子觸發切分。
   Q3=A：thead 明寫 display:table-header-group，鎖住「黃底表頭每頁重複」的現況不被誤改。
   已知取捨（Q1=A 已拍板接受、AC11 列為預期行為）：頁尾放不下整列時會留下最多約一列
   （144px）的空白，某些資料量下總頁數可能 +1。這是「頁尾留白」與「資料撕裂」二選一，
   本質上沒有第三種解。
   Q5=A：修正範圍僅限報價單 PDF；型錄 catalogPdfExport 是「一頁一卡 + page-break-after:always」
   結構本無此問題，不動。Excel 匯出（quoteExcelExport）亦零變更。
   驗證：tests/quotePdfPageBreak.fixture.js（靜態鎖 + headless Chrome --print-to-pdf
   逐頁比對，含「拿掉本規則就會紅」的反向對照，證明不是恆真的假綠燈）。 */
.items-table thead { display: table-header-group; }
.items-table tr, .items-table td { break-inside: avoid; page-break-inside: avoid; }

.product-cell { display: flex; align-items: center; gap: 12px; }
/* 需求 R91：產品圖區塊底色 #F0E0B2 → 白色（文字色 #8D6E63 維持不變）。
   有圖片時 object-fit:contain 留白處、無圖片時「產品圖」placeholder 皆改為白底。
   需求 R135 Q3=放大 + Q5=PDF 同步：縮圖 55px → 70px（與 Excel ITEM_THUMB_PX 同值）。
   代價（實測值，非估算）：.product-info 可用寬 = 產品資訊欄內容寬 − 70px 縮圖 − 12px gap，
   在本次的 30% 欄寬下為 125.7px（改前 34% + 55px 縮圖為 170.5px）。
   中文品名「日香-冬筍餅」72.9px 仍單行；英文品名「Jih Hsiang-Sweet Potato Cracker」172.8px
   改前改後同為兩行（需求 R122 已記載 34% 時 170.5 < 172.8 就已折行），不是本次新增的折行。
   若 PM 之後覺得品名折得太兇，最小代價的回退是縮圖改回 55px（可用寬回到 140.7px），
   欄寬不必動；此選項已在需求討論中列出。 */
.product-thumbnail {
  width: 70px; height: 70px; background: #fff; border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; color: #8D6E63; flex-shrink: 0;
}
.product-info { flex: 1; }
.product-name { font-weight: 500; font-size: 13px; color: #5D4037; margin-bottom: 1px; }
.product-name-en { font-size: 11px; color: #8D6E63; line-height: 1.3; }
.product-code {
  font-size: 11px; color: #6D4C41; background: #F0E0B2;
  padding: 1px 5px; border-radius: 3px; display: inline-block; margin-top: 2px;
}

.quotation-footer { padding: 24px; background: #FFFBF0; border-top: 1px solid #e6c9a3; }
.trade-terms {
  background: #fff; border: 1px solid #e6c9a3; border-radius: 4px; padding: 16px;
}
.trade-terms h4 { font-size: 14px; color: #8D6E63; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
.trade-terms p { font-size: 14px; color: #5D4037; line-height: 1.6; }
/* 需求 R161 Q2=A（PM 2026-09-06「要」）：頁尾「交易條件 TRADE TERMS」區塊
   也一併禁止跨頁切分——不然五條條款會被切成「前兩條在上一頁、後三條在下一頁」。
   .quotation-footer 與 .trade-terms 兩層都鎖：只鎖內層時，外層 footer 的 24px padding 仍可能
   被單獨留在前一頁的頁尾（視覺上是一條沒有內容的米色帶）。 */
.quotation-footer, .trade-terms { break-inside: avoid; page-break-inside: avoid; }

@media print {
  body { background: #fff; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .quotation-container { box-shadow: none; width: 100%; }
  @page { size: A4; margin: 0; }
}
</style>
</head>
<body>
<div class="quotation-container">
  <div class="quotation-header">
    <div class="header-top">
      <div class="company-info">
        <!-- 需求 R9：新增的公司沒有 Logo 時保持空白，不以公司名稱填補 -->
        <div class="company-logo">${co.logoUrl ? `<img src="${esc(co.logoUrl)}" alt="${esc(co.logo)}" />` : ''}</div>
        <div class="company-details">
          <h1>${esc(co.name)}</h1>
          <p class="company-name-en">${esc(co.nameEn)}</p>
          ${co.address ? `<p>${esc(co.address)}</p>` : ''}
          ${co.addressEn ? `<p>${esc(co.addressEn)}</p>` : ''}
          ${co.tel ? `<p>${esc(co.tel)}</p>` : ''}
        </div>
      </div>
      <div class="quotation-title">
        <h2>報價單｜QUOTATION</h2>
        ${contact.name ? `
        <div class="contact-display">
          <!-- 需求 R81 D2（PM 2026-07-22 拍板）：「聯絡人 Contact」區塊標題保留；僅移除欄位級標題（email 直接呈現值） -->
          <div class="contact-label">聯絡人 Contact</div>
          <div class="contact-name">${esc(contact.name)}</div>
          ${contact.email ? `<div class="contact-info">${esc(contact.email)}</div>` : ''}
        </div>` : ''}
      </div>
    </div>
    <div class="header-info">
      <div class="info-section">
        <h3>單據資料</h3>
        <div class="info-row"><span class="info-label">報價單編號</span><span class="info-value">${esc(quote.quoteNumber)}</span></div>
        <div class="info-row"><span class="info-label">報價日期</span><span class="info-value">${fmtDate(quote.quoteDate)}</span></div>
        <div class="info-row"><span class="info-label">有效期限</span><span class="info-value">${fmtDate(quote.expiryDate)}</span></div>
      </div>
      <div class="info-section">
        <h3>客戶資料</h3>
        <div class="info-row"><span class="info-label">客戶名稱</span><span class="info-value">${esc(quote.customerName || '—')}</span></div>
        <div class="info-row"><span class="info-label">聯絡電話</span><span class="info-value">${esc(custPhone || '—')}</span></div>
        <div class="info-row"><span class="info-label">電子信箱</span><span class="info-value">${esc(custEmail || '—')}</span></div>
      </div>
    </div>
  </div>

  <div class="quotation-body">
    <table class="items-table">
      <thead>
        <tr>
          <!-- 需求 R122：PM 2026-08-19 回「方案A」→ 五欄百分比改為
               產品資訊 35%→34% ／ 規格 18%→22% ／ 單價 17%→16% ／ 材積 15%（不動） ／ 備註 15%→13%（合計 100%）。
               起因：附圖中「MOQ: 單 10 箱 + 混 40 箱」被擠成三行（最後一個「箱」獨佔一行）。
               量測（headless Chrome，210mm 頁寬 − .quotation-body 左右 padding 24px = 表格實寬 745.7px）：
                 規格欄 18% = 134.2px < 該行單行需求 157.8px（缺 23.6px）→ 22% = 164.1px（餘裕 6.3px）不再換行。
               寬度來源（實測值，非估算）：
                 單價 16% → 內容可用寬 103.3px ≥ 最壞情境「NT$ 12,345.60/箱」99.6px → 仍單行。
                 備註 13% → 內容可用寬 81.0px ≥「植物五辛素」65.0px、6 字備註 78.0px → 仍單行。
                 材積 15% 不動 → 13 碼條碼 88.2px 仍單行。
               ⚠ 產品資訊 35%→34% 的已知取捨（本輪實測推翻本票描述原先寫的「英文品名本來就已換行」）：
                 品名的換行門檻是 .product-cell 內 .product-info 的寬（= td 內容寬 − 55px 縮圖 − 12px gap），
                 35% 時 178.0px ≥ 最長英文品名「Jih Hsiang-Sweet Potato Cracker」172.8px → 單行（餘裕僅 5.2px）；
                 34% 時 170.5px < 172.8px → 該英文品名會折成兩行。
                 列高由 55px 縮圖主導（前後同為 88.7px），不會撐高任一列，但確實多一行文字。
                 若 PM 不接受此折行，替代解是「產品資訊維持 35% + 備註改 12%（可用 73.5px）」——
                 代價換成 6 字以上備註會換行（現有資料「植物五辛素」5 字仍單行）。已於評論請 PM 確認。
               Q4=A（維持現況）：不加 white-space:nowrap，超長 MOQ 仍允許自然換行而非溢出壓到鄰欄。
                 22% 可容納到「MOQ: 單 10 箱 + 混 40 箱」該長度（157.8px）為止；再長（例如末尾加註
                 「(混櫃)」需 193.8px）仍會換行 —— item.moq 是自由輸入文字，任何固定欄寬都只能保證
                 「到某個長度為止不換行」，此為預期行為不是缺陷。
               Q5：材積、條碼欄維持 15%（其表頭英文 Cuft / CBM / Barcode 本來就折行，本案不處理）。
               Q3（Excel 匯出 quoteExcelExport.js 欄寬是否同步）PM 尚未回覆 → 本次只改 PDF。
               驗證：tests/quoteSpecColumnWidth.fixture.js（靜態斷言 + headless Chrome 前後量測）。
               ⚠ 需求 R135 已改寫本段的欄寬數字（34/22/16/15/13 → 30/22/16/16/16），見下一段註解；
                 上面這段保留是因為「規格 22% / 單價 16% 兩個單行門檻」的量測依據仍然有效。 -->
          <!-- 需求 R135 Q5（PM 2026-08-24「請同步調整」）：Excel 六欄改為
               [14,26,23,16,23,23]ch = 103/187/166/117/166/166px（合計 905px）。PDF 明細表只有五欄
               （Excel 的 A 縮圖欄 + B 產品文字欄在 PDF 是同一格 .product-cell），等比換算為
                 產品資訊 (103+187)/905 = 32.0% ／ 規格 18.3% ／ 單價 12.9% ／ 材積 18.3% ／ 備註 18.3%。
               但 PDF 表格實寬固定 745.7px（210mm − .quotation-body 左右 padding 24px），
               純等比會踩壞兩條已驗收的單行門檻（headless Chrome 實測，非估算）：
                 · 規格 18.3% = 118.2px 內容寬 → 「MOQ: 單 10 箱 + 混 40 箱」141.8px 又被折成兩行
                   ＝直接退回 需求 R122 修好之前的狀態。
                 · 單價 12.9% = 80.9px 內容寬 < 最壞情境「NT$ 12,345.60/箱」99.6px → 單價也會折行。
               因此採「等比為目標、已驗收門檻為約束」的分配 30 / 22 / 16 / 16 / 16（合計 100%）：
                 · 規格維持 22%（需求 R122 底線，內容寬 148px），單價維持 16%（內容寬 103.3px ≥ 99.6px）
                   —— 這也正好對應 Excel 端 D 欄「維持 16 不動」。
                 · 材積 15%→16%（95.8→103.3px）、備註 13%→16%（81.0→103.3px），忠實反映 PM 這次
                   把 E/F 兩欄從 14ch 大幅加寬到 23ch 的意圖。
                 · 差額由產品資訊 34%→30% 吸收（內容寬 237.5→207.7px）。
               驗證：tests/quoteExportColumnSize.fixture.js（靜態斷言 + headless Chrome 實測）。 -->
          <th style="width:30%">產品資訊<br><span class="th-en">Product Information</span></th>
          <!-- 需求 R121：PM 2026-08-19 拍 Q1=A「全部統一為『保存期限』」，
               本表頭原為「保質期」（全 repo 僅報價單 PDF/Excel 兩處這樣寫），改為「保存期限」，
               與型錄匯出／產品資料匯出／產品管理各畫面一致。英文 Shelf Life 不變。
               Q3「只改報價單」→ 型錄 catalogPdfExport 不動；Q4=B → 產品匯出 productExportWriter 不動
               （兩者本來就是「保存期限」，Q1=A 後全站一致）。
               Q2（資料列是否加「保存期限：」前綴）PM 回「不太理解問題」→ 維持現況不加前綴，
               資料列仍是 規格 / MOQ / 保存期限值 同格分行，待 PM 確認後再議。 -->
          <th class="text-center" style="width:22%">規格/MOQ/保存期限<br><span class="th-en">Spec / MOQ / Shelf Life</span></th>
          <th class="text-center" style="width:16%">單價<br><span class="th-en">Unit Price / CTN</span></th>
          <th class="text-center" style="width:16%">材積、條碼<br><span class="th-en">Cuft / CBM / Barcode</span></th>
          <th class="text-center" style="width:16%">備註<br><span class="th-en">Remark</span></th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </div>

  ${(() => {
    const notes = quote.transactionNotes || '1. 本報價單有效期限為30天。\n2. 付款條件：訂單確認後預付50%，出貨前付清尾款。\n3. 交貨條件：FOB 台北 (Taiwan)，運費另計。\n4. 交貨時間：訂單確認後 7-14 個工作天。\n5. 本報價不含稅金，如需開立發票請另加 5% 營業稅。';
    return `
  <div class="quotation-footer">
    <div class="trade-terms">
      <h4>交易條件 TRADE TERMS</h4>
      <p>${esc(notes).replace(/\n/g, '<br>')}</p>
    </div>
  </div>`;
  })()}
</div>
${autoPrint ? `<script>
  // 需求 R3：另開分頁後自動觸發列印預覽
  // 需求 R37：先等 load + document.fonts.ready 後再 print，避免 CDN 字型尚未就緒
  // 就被瀏覽器以系統字回退（macOS / Windows 中文版以外環境會出現 □ 或亂碼）。
  // 若 fonts API 不可用或 2s 內未 resolve，仍以 setTimeout 兜底觸發列印。
  (function () {
    var printed = false;
    function triggerPrint() {
      if (printed) return;
      printed = true;
      try { window.focus(); } catch (e) {}
      window.print();
    }
    function whenFontsReady() {
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        var fallback = setTimeout(triggerPrint, 2000);
        document.fonts.ready.then(function () {
          clearTimeout(fallback);
          triggerPrint();
        }).catch(function () {
          clearTimeout(fallback);
          triggerPrint();
        });
      } else {
        setTimeout(triggerPrint, 300);
      }
    }
    if (document.readyState === 'complete') {
      whenFontsReady();
    } else {
      window.addEventListener('load', whenFontsReady);
    }
  })();
</script>` : ''}
</body>
</html>`;
}
